import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Card, Button, Table, Form, Modal, Spinner, Alert, Badge
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSave, FaTimes, FaClipboardCheck
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const BASE_ENDPOINT = '/api/current-exam-pass-types';

const CurrentExamPassType = () => {
  const navigate = useNavigate();
  const [passTypes, setPassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    exam_pass_name: '',
    exam_pass_desc: '',
    is_active: true
  });

  const getItemId = (item) => (
    item?.current_exam_pass_type_id ||
    item?.exam_pass_type_id ||
    item?.id ||
    item?._id
  );

  const extractList = (response) => {
    const message = response?.message;
    if (Array.isArray(message?.currentExamPassTypes)) return message.currentExamPassTypes;
    if (Array.isArray(message?.examPassTypes)) return message.examPassTypes;
    if (Array.isArray(message?.data)) return message.data;
    if (Array.isArray(message)) return message;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const extractItem = (response) => {
    const message = response?.message;
    if (message?.currentExamPassType && typeof message.currentExamPassType === 'object') return message.currentExamPassType;
    if (message?.examPassType && typeof message.examPassType === 'object') return message.examPassType;
    if (message?.data && typeof message.data === 'object') return message.data;
    if (message && typeof message === 'object' && !Array.isArray(message)) return message;
    return null;
  };

  const fetchPassTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(BASE_ENDPOINT);
      setPassTypes(extractList(response));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load current exam pass types.');
      setPassTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPassTypes();
  }, [fetchPassTypes]);

  const fetchByIdAndOpenModal = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(`${BASE_ENDPOINT}/${id}`);
      const item = extractItem(response);

      if (!item) {
        setError('Unable to fetch selected record.');
        return;
      }

      setFormData({
        exam_pass_name: item.exam_pass_name || '',
        exam_pass_desc: item.exam_pass_desc || '',
        is_active: typeof item.is_active === 'boolean' ? item.is_active : true
      });
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch selected record.');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setCurrentId(null);
    setFormData({
      exam_pass_name: '',
      exam_pass_desc: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = async (item) => {
    const id = getItemId(item);
    if (!id) {
      setError('Invalid record ID.');
      return;
    }
    setIsEditMode(true);
    setCurrentId(id);
    await fetchByIdAndOpenModal(id);
  };

  const handleSave = async () => {
    if (!formData.exam_pass_name.trim()) {
      setError('Exam pass name is required.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        exam_pass_name: formData.exam_pass_name.trim(),
        exam_pass_desc: formData.exam_pass_desc.trim(),
        is_active: Boolean(formData.is_active)
      };

      let response;
      if (isEditMode && currentId) {
        response = await apiService.put(`${BASE_ENDPOINT}/${currentId}`, payload);
      } else {
        response = await apiService.post(BASE_ENDPOINT, payload);
      }

      if (response?.success) {
        setSuccessMsg(isEditMode ? 'Current exam pass type updated successfully.' : 'Current exam pass type created successfully.');
        setShowModal(false);
        await fetchPassTypes();
      } else {
        setError(response?.message || 'Operation failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      setError('Invalid record ID.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this pass type?')) return;

    try {
      setError(null);
      const response = await apiService.delete(`${BASE_ENDPOINT}/${id}`);
      if (response?.success) {
        setSuccessMsg('Current exam pass type deleted successfully.');
        await fetchPassTypes();
      } else {
        setError(response?.message || 'Failed to delete record.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete record.');
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" className="mb-2" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
          <h2 className="mb-0">
            <FaClipboardCheck className="me-2 text-primary" />
            Current Exam Pass Type
          </h2>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <FaPlus className="me-2" /> Add Pass Type
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Pass Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {passTypes.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">No records found.</td>
                  </tr>
                ) : (
                  passTypes.map((item, index) => {
                    const id = getItemId(item);
                    return (
                      <tr key={id || index}>
                        <td>{index + 1}</td>
                        <td><strong>{item.exam_pass_name || '-'}</strong></td>
                        <td>{item.exam_pass_desc || '-'}</td>
                        <td>
                          <Badge bg={item.is_active ? 'success' : 'secondary'}>
                            {item.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEditModal(item)}>
                            <FaEdit />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(id)}>
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Pass Type' : 'Add Pass Type'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Exam Pass Name *</Form.Label>
              <Form.Control
                value={formData.exam_pass_name}
                onChange={(e) => setFormData({ ...formData, exam_pass_name: e.target.value })}
                placeholder="e.g. N5 Passed"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.exam_pass_desc}
                onChange={(e) => setFormData({ ...formData, exam_pass_desc: e.target.value })}
                placeholder="e.g. Candidate has passed JLPT N5"
              />
            </Form.Group>

            <Form.Group>
              <Form.Check
                type="switch"
                id="is-active-switch"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                {isEditMode ? 'Update' : 'Create'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CurrentExamPassType;
